import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Analytics16, WarningFill16 } from 'ui/icons';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { TopTooltip } from 'ui/tooltip';
import { borderColors, colors, spacing, zIndex, Body2 } from 'ui/theme';
import { useModalContext } from 'utils/contexts/modal';
import { OptimizeModeLimit } from 'modules/frequencyManagement/lib/constants';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';
import { Body2Underline } from 'modules/frequencyManagement/components/Content.styles';
import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { useOptimizeModeTotal } from 'modules/frequencyManagement/hooks/useOptimizeModeTotal';
import { useOnSubmit } from 'modules/frequencyManagement/hooks/useOnSubmit';
import { DISCARD_CHANGES_MODAL_ID } from 'modules/frequencyManagement/components/layout/DiscardChangesModal';
import {
  FrequencyManagementSchema,
  MessageTypeSchema,
} from 'modules/frequencyManagement/contexts/FormProvider';
import { deriveDirtyState } from 'modules/frequencyManagement/contexts/contextUtils';
import {
  selectIsFrequencyManageOptimizeFlagOn,
  selectIsFrequencyOptimizationReportingFlagOn,
} from 'core/userContext';
import { ConfirmChangesModalId } from './UnsavedChangesModal';
import { useNavigate } from 'react-router-dom-v5-compat';
import { IntelligentInsightsTab } from 'modules/intelligentInsights/constants';

const Wrapper = styled.div`
  align-items: center;
  background-color: ${colors.white};
  border-top: solid ${spacing.single} ${borderColors.borderPrimary};
  bottom: 0;
  display: flex;
  justify-content: space-between;
  left: 0;
  padding: ${spacing.base} ${spacing.plusHalf};
  position: fixed;
  right: 0;
  width: 100%;
  z-index: ${zIndex.drawer};
`;

const Right = styled.div`
  align-items: center;
  display: flex;
  gap: ${spacing.base};
`;

const Body2NoMargin = styled(Body2)`
  color: ${colors.textSecondary};
  margin-bottom: 0;
`;

const InlineFlex = styled.div`
  align-items: center;
  display: inline-flex;
  gap: ${spacing.half};
`;

const PopoverContentTranslated = styled(PopoverContent)`
  transform: translate(${spacing.base}, 0);
`;

export const disabledTooltipContent =
  'Please address the persisting errors on the page prior to attempting to save';

export const Footer = ({ messageMedium }: { messageMedium: SupportedMediums }) => {
  const navigate = useNavigate();
  const {
    formState: { isValid, dirtyFields },
    getValues,
  } = useFormContext<FrequencyManagementSchema>();
  const isOptimizeEnabled = useSelector(selectIsFrequencyManageOptimizeFlagOn);
  const isFroReportingEnabled = useSelector(selectIsFrequencyOptimizationReportingFlagOn);
  const { loading } = useOnSubmit(messageMedium);
  const { showModal } = useModalContext();
  const { totalOptimizeCount } = useOptimizeModeTotal(messageMedium);

  const { mode, messageTypes } = getValues();

  const areFieldsDirtyOmitMode = deriveDirtyState(
    mode,
    dirtyFields,
    messageTypes as MessageTypeSchema[],
    false,
  );
  const areFieldsDirtyWithMode = deriveDirtyState(
    mode,
    dirtyFields,
    messageTypes as MessageTypeSchema[],
    true,
  );

  const navigateToFroReporting = () => {
    navigate(`/intelligentInsights/${IntelligentInsightsTab.FRO}`);
  };

  return (
    <Wrapper>
      {isOptimizeEnabled ? (
        <InlineFlex>
          <Popover>
            <PopoverContentTranslated
              TitleContent={`${totalOptimizeCount} of ${OptimizeModeLimit} optimizations used.`}
            >
              There is max of 10 optimizations allowed per project across all mediums.
            </PopoverContentTranslated>
            <PopoverTrigger>
              <Body2Underline>
                {totalOptimizeCount} of {OptimizeModeLimit} optimizations used.
              </Body2Underline>
            </PopoverTrigger>
          </Popover>
          {isFroReportingEnabled && (
            <SecondaryButton onClick={navigateToFroReporting} size={BUTTON_SIZES.SMALL}>
              <Analytics16 />
              View Performance
            </SecondaryButton>
          )}
        </InlineFlex>
      ) : (
        <div></div>
      )}
      <Right>
        {areFieldsDirtyWithMode && (
          <InlineFlex>
            {/* NOTE: Show only if unsaved changes are present */}
            <WarningFill16 color={colors.warning100} />
            <Body2NoMargin>Unsaved changes</Body2NoMargin>
          </InlineFlex>
        )}
        <SecondaryButton
          onClick={() => {
            showModal(DISCARD_CHANGES_MODAL_ID);
          }}
          size={BUTTON_SIZES.LARGE}
          isDisabled={!areFieldsDirtyWithMode}
        >
          Cancel
        </SecondaryButton>
        <TopTooltip
          isEnabled={areFieldsDirtyOmitMode && !isValid}
          tooltipContent={disabledTooltipContent}
        >
          <PrimaryButton
            size={BUTTON_SIZES.LARGE}
            isDisabled={!areFieldsDirtyOmitMode || !isValid || loading}
            isLoading={loading}
            onClick={() => {
              showModal(ConfirmChangesModalId);
            }}
          >
            Save changes
          </PrimaryButton>
        </TopTooltip>
      </Right>
    </Wrapper>
  );
};
